/**
 * AutomationModeContext
 *
 * Global React context for automation mode (Pilot/Co-Pilot/Autopilot).
 * Provides single source of truth for mode state across the application.
 *
 * Part of Bucket 1: Mode Infrastructure
 * Epic: APB-1054 - [SOAR] Co-Pilot Activation
 *
 * Usage:
 *   // In App.tsx
 *   <AutomationModeProvider>
 *     <AppRoutes />
 *   </AutomationModeProvider>
 *
 *   // In any component
 *   const { mode, modeDisplayName, modeStyles, isLoading } = useAutomationMode();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { Rocket, ShieldCheck, Zap, LucideIcon } from 'lucide-react';
import {
  automationModeService,
  AutomationMode,
  AutomationModeResponse,
} from '../services/automationModeService';

// Re-export the type for convenience
export type { AutomationMode } from '../services/automationModeService';

// =============================================================================
// Types
// =============================================================================

export interface ModeDisplayInfo {
  name: string;
  description: string;
  /** Lucide icon component for this mode */
  Icon: LucideIcon;
  color: string;
  styles: string;
}

export interface AutomationModeContextValue {
  /** Current automation mode */
  mode: AutomationMode;
  /** Whether mode is currently being fetched */
  isLoading: boolean;
  /** Error message if mode fetch failed */
  error: string | null;
  /** Whether SOAR is enabled for the tenant */
  soarEnabled: boolean;
  /** Display name for the current mode (e.g., "Pilot", "Co-Pilot", "Auto-Pilot") */
  modeDisplayName: string;
  /** Tailwind CSS classes for mode styling */
  modeStyles: string;
  /** Full display info for the current mode */
  modeInfo: ModeDisplayInfo;
  /** Refresh the mode from the server */
  refreshMode: () => Promise<void>;
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Mode display configuration - single source of truth for mode presentation
 */
export const MODE_DISPLAY_INFO: Record<AutomationMode, ModeDisplayInfo> = {
  pilot: {
    name: 'Pilot',
    description: 'Manual mode - BRAIN suggests actions, analysts execute.',
    Icon: Rocket,
    color: 'blue',
    styles: 'bg-blue-500/20 border-blue-500/50 text-blue-400',
  },
  copilot: {
    name: 'Co-Pilot',
    description: 'Approval mode - BRAIN prepares actions, analysts approve with 2FA.',
    Icon: ShieldCheck,
    color: 'amber',
    styles: 'bg-amber-500/20 border-amber-500/50 text-amber-400',
  },
  autopilot: {
    name: 'Auto-Pilot',
    description: 'Autonomous mode - BRAIN executes within guardrails, escalates critical.',
    Icon: Zap,
    color: 'emerald',
    styles: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400',
  },
};

// =============================================================================
// Context
// =============================================================================

const AutomationModeContext = createContext<AutomationModeContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

interface AutomationModeProviderProps {
  children: ReactNode;
  /** Optional initial mode for testing */
  initialMode?: AutomationMode;
}

/**
 * Returns true if there is an auth token in localStorage. Used to skip the
 * mode fetch on unauthenticated pages (e.g. /login), which otherwise spams
 * the console with `[AutomationModeContext] Failed to fetch mode: API Error`.
 */
const hasAuthToken = (): boolean => {
  if (typeof window === 'undefined') return false;
  const token = window.localStorage.getItem('astrapeak_auth_token');
  return !!token && token.length > 0;
};

export const AutomationModeProvider: React.FC<AutomationModeProviderProps> = ({
  children,
  initialMode,
}) => {
  const [mode, setMode] = useState<AutomationMode>(initialMode || 'pilot');
  const [isLoading, setIsLoading] = useState(!initialMode);
  const [error, setError] = useState<string | null>(null);
  const [soarEnabled, setSoarEnabled] = useState(true);

  /**
   * Fetch the current mode from the server.
   *
   * Skips when no auth token is present — useful for routes that mount the
   * provider above the auth boundary (e.g. /login). Without this guard the
   * provider would hammer `/api/automation/mode` on every page load.
   */
  const fetchMode = useCallback(async () => {
    if (!hasAuthToken()) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response: AutomationModeResponse = await automationModeService.getMode();
      setMode(response.mode);
      setSoarEnabled(response.soar_enabled);
    } catch (err) {
      console.error('[AutomationModeContext] Failed to fetch mode:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch automation mode');
      // Keep current mode on error (defaults to 'pilot' if never fetched)
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Public method to refresh mode (e.g., after admin changes it, or after
   * login completes). Callers can use this to retry after auth becomes
   * available.
   */
  const refreshMode = useCallback(async () => {
    await fetchMode();
  }, [fetchMode]);

  // Fetch mode on mount
  useEffect(() => {
    if (!initialMode) {
      fetchMode();
    }
  }, [fetchMode, initialMode]);

  // Refresh mode when tab becomes visible (handles mode changes in other tabs/admin)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !initialMode && hasAuthToken()) {
        fetchMode();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMode, initialMode]);

  // Refresh mode when localStorage auth token changes — handles the
  // "user just signed in" transition without requiring a full page reload.
  useEffect(() => {
    if (initialMode) return;
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'astrapeak_auth_token' && e.newValue && !e.oldValue) {
        fetchMode();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchMode, initialMode]);

  // Memoized display info
  const modeInfo = useMemo(() => MODE_DISPLAY_INFO[mode], [mode]);
  const modeDisplayName = useMemo(() => modeInfo.name, [modeInfo]);
  const modeStyles = useMemo(() => modeInfo.styles, [modeInfo]);

  // Memoized context value to prevent unnecessary re-renders
  const contextValue = useMemo<AutomationModeContextValue>(
    () => ({
      mode,
      isLoading,
      error,
      soarEnabled,
      modeDisplayName,
      modeStyles,
      modeInfo,
      refreshMode,
    }),
    [mode, isLoading, error, soarEnabled, modeDisplayName, modeStyles, modeInfo, refreshMode]
  );

  return (
    <AutomationModeContext.Provider value={contextValue}>
      {children}
    </AutomationModeContext.Provider>
  );
};

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access the automation mode context.
 *
 * @throws Error if used outside of AutomationModeProvider
 *
 * @example
 * const { mode, modeDisplayName, modeStyles, isLoading } = useAutomationMode();
 *
 * return (
 *   <span className={modeStyles}>
 *     Mode: {modeDisplayName}
 *   </span>
 * );
 */
export function useAutomationMode(): AutomationModeContextValue {
  const context = useContext(AutomationModeContext);

  if (context === undefined) {
    throw new Error('useAutomationMode must be used within an AutomationModeProvider');
  }

  return context;
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get display info for a specific mode (useful outside of React components)
 */
export function getModeDisplayInfo(mode: AutomationMode): ModeDisplayInfo {
  return MODE_DISPLAY_INFO[mode];
}

/**
 * Check if a mode allows auto-execution (Autopilot only)
 */
export function modeAllowsAutoExecution(mode: AutomationMode): boolean {
  return mode === 'autopilot';
}

/**
 * Check if a mode requires approval (Co-Pilot only)
 */
export function modeRequiresApproval(mode: AutomationMode): boolean {
  return mode === 'copilot';
}

/**
 * Check if a mode requires manual execution (Pilot only)
 */
export function modeRequiresManualExecution(mode: AutomationMode): boolean {
  return mode === 'pilot';
}

export default AutomationModeContext;
